import React, { Dispatch, DispatchWithoutAction, SetStateAction, useCallback, useEffect, useRef } from "react";
import { IDisable, IMutator, Nullable, TriggeredHook } from "sonobello.utilities.react";

import EnvironmentConfiguration from "../../../types/EnvironmentConfiguration";
import { ContactResult } from "../../../types/ContactResult";
import { DisqualifyingReason, DisqualifyingReasonsType } from "../../../types/DisqualifyingReason";
import { Lead } from "../../../types/Lead";
import { Step } from "../../../types/Step";
import IBookingController from "../../../Calendar/types/IBookingController";
import { IFlowStepProps } from "../../../Flows/types/IFlow";
import { IPatient, evaluatePatientDisqualification } from "../../../types/Entities/Patient";
import ICenter from "../../../types/ICenter";
import IPatientQualification from "../types/IPatientQualification";
import StepWrapper, { IStepWrapperButtonProps } from "../../Components/StepWrapper";

export interface IUseCreateNonCandidateContactResultTrigger {
  /** The id of the patient. */
  patientId: string;
  /** The id of the patient's lead. */
  patientLeadId: string;
  /** The reason why the patient is disqualified. */
  disqualificationReason: DisqualifyingReasonsType;
  /** The list of possible disqualification reasons. */
  disqualifyingReasons: DisqualifyingReason[];
}

export interface IQualifyStepWrapperProps {
  /** The current state of the session's patient. */
  patient: IPatient;
  /** The center that was originally assigned to the session. */
  defaultCenter: ICenter;
  /** The current state of the bookable centers for the session. */
  bookingController: Nullable<Pick<IBookingController, "getTrackedCenters">>;
  /** The list of available disqualifying reasons, which is null until loaded from the cache or the server. */
  disqualifyingReasons: Nullable<DisqualifyingReason[]>;
  /** The current session's contact identifier. */
  contactId: string;
  /** A callback to be executed when the patient is confirmed as a non-candidate. */
  onNonCandidateDisqualification: DispatchWithoutAction;
  /** The function which updates the session's patient state. */
  setPatient: Dispatch<SetStateAction<IPatient>>;
  /** The function which updates the session's contact result state. */
  setContactResult: Dispatch<ContactResult>;
  /** Resolves the next step from flow configuration using current app-context state. */
  getNextStep: () => Step | undefined;
}

/** An updated {@link IPatient} model containing the guaranteed qualification information. */
export interface IPatientUpdate {
  centerId: string;
  dateOfBirth: string;
  distanceToCenter?: number;
  id: string;
  email?: string;
  firstName: string;
  height: { inches: number; feet: number };
  lastName: string;
  lead: Lead;
  phoneNumber?: string;
  weight: number;
  zipCode?: string;
  isPreviouslyQualified: boolean;
  qualificationDateUtc?: string;
}

export interface IQualifyStepWrapperConfig {
  /** The hook which creates a non-candidate contact result for the session. */
  UseCreateNonCandidateContactResult: TriggeredHook<
    ContactResult,
    IUseCreateNonCandidateContactResultTrigger,
    boolean,
    { contactId: string }
  >;
  /** The hook which updates the persisted patient state. */
  UseUpdatePatient: TriggeredHook<
    IPatient | null,
    IPatientUpdate,
    boolean,
    { contactId: string; currentPatient: IPatient }
  >;
  /** The component which should be rendered within this wrapper. */
  QualifyStep: React.FC<IDisable & IMutator<IPatientQualification>>;
}

const QualifyStepWrapper: React.FC<
  IQualifyStepWrapperConfig & IQualifyStepWrapperProps & IStepWrapperButtonProps & IFlowStepProps
> = ({
  patient,
  defaultCenter,
  bookingController,
  disqualifyingReasons,
  contactId,
  onNonCandidateDisqualification,
  setPatient,
  setContactResult,
  getNextStep,
  UseCreateNonCandidateContactResult,
  UseUpdatePatient,
  QualifyStep,
  setStep,
  ...props
}) => {
  const handledUpdatePatientResultRef = useRef<IPatient | boolean | null>(null);
  const {
    result: updatePatientResult,
    isLoading: updatePatientIsLoading,
    execute: executeUpdatePatient,
    error: updatePatientError
  } = UseUpdatePatient({ contactId, currentPatient: patient });
  const {
    result: createContactResultResult,
    isLoading: createContactResultIsLoading,
    execute: executeCreateContactResult,
    error: createContactResultError
  } = UseCreateNonCandidateContactResult({ contactId });

  const onClickNext = () => {
    setPatient(c => {
      if (!c.height || c.weight === undefined || !c.dateOfBirth)
        throw new Error("Patient must have height/weight/dateOfBirth to continue.");
      if (!disqualifyingReasons) throw new Error("Disqualifying reasons must be loaded to continue.");
      const updatedPatient = {
        ...(c as IPatient & Required<Pick<IPatient, "height" | "weight" | "dateOfBirth">>),
        distanceToCenter:
          c.distanceToCenter ??
          bookingController?.getTrackedCenters().find(c => c.id === defaultCenter.id)?.distance ??
          EnvironmentConfiguration.optimizedPatientScheduler.fallbackPatientDistanceToCenter
      };
      const disqualificationReason = evaluatePatientDisqualification(updatedPatient);
      if (disqualificationReason) {
        onNonCandidateDisqualification();
        executeCreateContactResult({
          patientId: patient.id,
          patientLeadId: patient.lead.id,
          disqualificationReason,
          disqualifyingReasons
        });
      }
      executeUpdatePatient(updatedPatient);
      return updatedPatient;
    });
  };

  useEffect(() => {
    if (updatePatientError) throw new Error("Failed to update patient.");
    if (createContactResultError) throw new Error("Failed to create non-candidate contact result.");
  }, [createContactResultError, updatePatientError]);

  useEffect(() => {
    if (updatePatientIsLoading || createContactResultIsLoading || !disqualifyingReasons) return;
    if (createContactResultResult) setContactResult(createContactResultResult);
    if (updatePatientResult && !createContactResultResult) {
      if (handledUpdatePatientResultRef.current === updatePatientResult) return;
      handledUpdatePatientResultRef.current = updatePatientResult;
      if (typeof updatePatientResult === "object") setPatient(c => ({ ...c, ...updatePatientResult }));
      const nextStep = getNextStep();
      if (nextStep === undefined) throw new Error("Flow configuration did not produce a next step from qualify.");
      setStep(nextStep);
    }
  }, [
    updatePatientIsLoading,
    createContactResultIsLoading,
    updatePatientResult,
    createContactResultResult,
    disqualifyingReasons,
    setPatient,
    getNextStep
  ]);

  const onChange = useCallback(
    (value: IPatientQualification) =>
      setPatient(c => ({
        ...c,
        height: value.height,
        weight: value.weightInPounds,
        dateOfBirth: value.dateOfBirth.toISODate() || undefined,
        qualificationDateUtc: value.timeStampUtc.toISODate() || undefined
      })),
    [setPatient]
  );

  return (
    <StepWrapper
      {...props}
      nextButtonLoading={
        updatePatientIsLoading ||
        createContactResultIsLoading ||
        !bookingController?.getTrackedCenters().length ||
        !disqualifyingReasons
      }
      onNext={onClickNext}
      disabled={!patient.height || patient.weight === undefined || !patient.dateOfBirth}
    >
      <QualifyStep disabled={updatePatientIsLoading || createContactResultIsLoading} onChange={onChange} />
    </StepWrapper>
  );
};

export default QualifyStepWrapper;
